import * as React from "react";
import axios from "axios";
import Header from "../../components/Header";
import SimpleTable from "../../components/SimpleTable";
import {
  Box,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Divider,
  TextField,
  MenuItem,
  useTheme,
  InputAdornment,
 
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { tokens } from "../../theme";
import PermissionGuard from "../../components/PermissionGuard";

/* ---------- helpers ---------- */
// Format ISO datetime string for display as "YYYY-MM-DD HH:mm" without timezone shift
const fmtDateTime = (iso) => {
  if (!iso) return "-";
  const [date, time] = iso.split("T");
  return `${date} ${time.slice(0, 5)}`; // "YYYY-MM-DD HH:mm"
};

// Format ISO date string for display as "YYYY-MM-DD"
const fmtDate = (iso) => {
  if (!iso) return "-";
  return iso.split("T")[0];
};

// For <input type="date">, extract "YYYY-MM-DD"
const toDateInput = (iso) => (iso ? iso.split("T")[0] : "");

// Convert "YYYY-MM-DD" from date input to UTC ISO string
const fromDateInputToISO = (yyyyMmDd) => {
  if (!yyyyMmDd) return null; // send null to clear
  return `${yyyyMmDd}T00:00:00.000Z`;
};

const show = (v) =>
  v === null || v === undefined || v === "" ? "-" : String(v).trim();
const toNum = (v) =>
  v === "" || v === null || v === undefined ? "" : Number(v);

// normalize option rows from any API shape into {id, name}
const normalizeList = (list, nameKeys = ["name", "fullName", "title"]) => {
  if (!Array.isArray(list)) return [];
  return list
    .map((o) => {
      const id = o?.id ?? o?.ID ?? o?.Id ?? o?.uuid ?? o?.categoryId;
      const key = nameKeys.find((k) => o && o[k] != null);
      const name = o?.[key] ?? o?.label ?? o?.code ?? `#${id ?? "?"}`;
      return { id, name };
    })
    .filter((o) => o.id !== undefined && o.id !== null);
};

// keep current ID selectable even if not in fetched list
function ensureOption(options, id) {
  if (id === "" || id === null || id === undefined) return options || [];
  const nid = Number(id);
  const exists = (options || []).some((o) => Number(o.id) === nid);
  return exists ? options : [{ id: nid, name: `#${nid}` }, ...(options || [])];
}

// lookup helper to display Category name from id
const nameById = (options, id) => {
  const n = (options || []).find((o) => Number(o.id) === Number(id));
  return n?.name ?? "-";
};

/* ---------- API ---------- */
const token = localStorage.getItem("token");

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
});

async function getJSON(path, params) {
  try {
    const res = await api.get(path, {
      params: { _t: Date.now(), ...(params || {}) },
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}
async function putJSON(path, body) {
  try {
    const res = await api.put(path, body, {
      
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;
    return { data: null, error: msg };
  }
}
async function deleteJSON(path) {
  try {
    const res = await api.delete(path, {
      
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;
    return { data: null, error: msg };
  }
}

const UNIT_OPTIONS = [
  "bottle",
  "pack",
  "box",
  "piece",
  "kg",
  "g",
  "dozen",
  "crate",
  "carton",
  "bag",
].map((u) => ({ value: u, label: u }));

/* ---------- page ---------- */
export default function ViewItem() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // table data & ui
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState({
    open: false,
    msg: "",
    sev: "success",
  });

  // dialogs
  const [detailRow, setDetailRow] = React.useState(null);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editRow, setEditRow] = React.useState(null);
  const [editValues, setEditValues] = React.useState({
    categoryId: "",
    name: "",
    unit: "",
   
    salePrice: "",
    minQuantity: "",
    description: "",
    expirationDate: "",
    
  });
  const [saving, setSaving] = React.useState(false);

  const [deleteRow, setDeleteRow] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  // dropdown options
  const [categories, setCategories] = React.useState([]);
  const [optsLoading, setOptsLoading] = React.useState(false);

  // fetch list (cache-busted)
  const load = React.useCallback(async () => {
    setLoading(true);
    const { data, error } = await getJSON("/api/item");
    setLoading(false);
    if (error) {
      setToast({ open: true, msg: "Failed to load items", sev: "error" });
      setRows([]);
      return;
    }
    setRows(Array.isArray(data?.data) ? data.data : []);
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  // fetch category options (cache-busted)
  const loadOptions = React.useCallback(async () => {
    setOptsLoading(true);
    const catRes = await getJSON("/api/category");
    setOptsLoading(false);

    setCategories(normalizeList(catRes.data?.data));
  }, []);

  React.useEffect(() => {
    // fetch once on mount
    loadOptions();
  }, [loadOptions]);

  // columns to display in table
  const columns = [
    {
      field: "no",
      headerName: "No.",
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (params) => {
        // index among visible rows on the current page
        const visibleIndex = params.api.getRowIndexRelativeToVisibleRows(
          params.id
        );
        // page & pageSize for absolute numbering across pages
        const pm = params.api?.state?.pagination?.paginationModel || {
          page: 0,
          pageSize: rows.length || 0,
        };
        const pageOffset = (pm.page || 0) * (pm.pageSize || 0);
        return pageOffset + visibleIndex + 1;
      },
    },
    { field: "id", headerName: "ID", width: 70, hide: true },
    {
      field: "name",
      headerName: "Name",
      flex: 1.2,
      minWidth: 200,
    },
    {
      field: "category",
      headerName: "Category",
      flex: 1,
      minWidth: 160,
      valueGetter: (p) => nameById(categories, p.row.categoryId),
    },
    { field: "unit", headerName: "Unit", width: 110 },
    
    { field: "salePrice", headerName: "Sale Price", width: 120 },

    {
      field: "actions",
      headerName: "Actions",
      width: 170,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Details">
              <IconButton size="small" onClick={() => setDetailRow(row)}>
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>

            <PermissionGuard
  module="Item"
  action="update"
>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={async () => {
                  setEditRow(row);
                  setEditValues({
                    categoryId: toNum(row.categoryId),
                    name: row.name ?? "",
                    unit: row.unit ?? "",
                    
                    salePrice: row.salePrice ?? "",
                    minQuantity: row.minQuantity ?? "",
                    description: row.description ?? "",
                    expirationDate: toDateInput(row.expirationDate),
                   
                  });
                  // ensure latest categories before opening dialog
                  await loadOptions();
                  setEditOpen(true);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            </PermissionGuard>

            <PermissionGuard
  module="Item"
  action="delete"
>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteRow(row)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
            </PermissionGuard>
          </Box>
        );
      },
    },
  ];

  // grid look (theme-consistent)
  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": {
      color: `${colors.greenAccent[200]} !important`,
    },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };

  // update (optimistic + refetch)
  const handleUpdate = async () => {
    if (!editRow?.id) return;
    setSaving(true);

    const payload = {
      categoryId: Number(editValues.categoryId),
      name: String(editValues.name ?? "").trim(),
      unit: String(editValues.unit ?? "").trim(),
     
      salePrice: Number(editValues.salePrice),
      minQuantity: Number(editValues.minQuantity),
      description: String(editValues.description ?? "").trim(),
      
    };

    const { data, error } = await putJSON(`/api/item/${editRow.id}`, payload);
    setSaving(false);

    if (error) {
      setToast({ open: true, msg: "Update failed", sev: "error" });
      return;
    }

    // PUT may return updated object directly (your sample shows direct object)
    const updated = data?.data || data;
    const merged = updated?.id ? updated : { id: editRow.id, ...payload };

    setRows((prev) =>
      prev.map((r) => (r.id === editRow.id ? { ...r, ...merged } : r))
    );

    setToast({ open: true, msg: "Item updated successfully", sev: "success" });
    setEditOpen(false);
    setEditRow(null);

    // safe refetch for canonical state + latest category names
    await Promise.all([load(), loadOptions()]);
  };

  // delete (optimistic + refetch)
  const handleDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);

    const { data, error } = await deleteJSON(`/api/item/${deleteRow.id}`);
    setDeleting(false);

    if (error) {
      setToast({ open: true, msg: "Delete failed", sev: "error" });
      return;
    }

    setRows((prev) => prev.filter((r) => r.id !== deleteRow.id));

    setToast({
      open: true,
      msg: data?.message || "Item deleted successfully",
      sev: "success",
    });
    setDeleteRow(null);

    await Promise.all([load(), loadOptions()]);
  };

  return (
    <>
      <Box m="20px">
        <Header title="ITEMS" subtitle="View, edit, and manage items" />

        <Box m="40px 0 0 0">
          <SimpleTable
            rows={rows}
            columns={columns}
            loading={loading}
            height="75vh"
            getRowId={(r) => r.id}
            sx={gridSx}
          />
        </Box>
      </Box>

      {/* Details */}
      <Dialog
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Item Details</DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <LabelValue
                  label="Category"
                  value={nameById(categories, detailRow.categoryId)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <LabelValue label="Name" value={show(detailRow.name)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <LabelValue label="Unit" value={show(detailRow.unit)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <LabelValue
                  label="Min Quantity"
                  value={show(detailRow.minQuantity)}
                />
              </Grid>

             
              <Grid item xs={12} sm={3}>
                <LabelValue
                  label="Sale Price"
                  value={show(detailRow.salePrice)}
                />
              </Grid>
             
              <Grid item xs={12} sm={3}>
                <LabelValue
                  label="Expiry"
                  value={fmtDate(detailRow.expirationDate)}
                />
              </Grid>

              <Grid item xs={12}>
                <LabelValue
                  label="Description"
                  value={show(detailRow.description)}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Item</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            {/* Category dropdown + refresh */}
            <TextField
              label={
                optsLoading
                  ? "Loading categories..."
                  : categories.length
                  ? "Category"
                  : "No categories found (refresh)"
              }
              select
              value={editValues.categoryId}
              onChange={(e) =>
                setEditValues((s) => ({
                  ...s,
                  categoryId: toNum(e.target.value),
                }))
              }
              fullWidth
              variant="filled"
              disabled={optsLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Refresh categories">
                      <span>
                        <IconButton
                          size="small"
                          onClick={loadOptions}
                          disabled={optsLoading}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            >
              {ensureOption(categories, editValues.categoryId).map((c) => (
                <MenuItem key={c.id} value={Number(c.id)}>
                  {c.name ?? `#${c.id}`}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Name"
              value={editValues.name}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, name: e.target.value }))
              }
              fullWidth
              variant="filled"
            />

            <TextField
              select
              label="Unit"
              value={editValues.unit}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, unit: e.target.value }))
              }
              fullWidth
              variant="filled"
            >
              {UNIT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            

            <TextField
              label="Sale Price"
              type="number"
              value={editValues.salePrice}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, salePrice: e.target.value }))
              }
              fullWidth
              variant="filled"
              inputProps={{ step: "0.01", min: 0 }}
            />

            <TextField
              label="Min Quantity"
              type="number"
              value={editValues.minQuantity}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, minQuantity: e.target.value }))
              }
              fullWidth
              variant="filled"
              inputProps={{ step: "1", min: 0 }}
            />

            <TextField
              label="Expiration Date"
              type="date"
              value={editValues.expirationDate}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, expirationDate: e.target.value }))
              }
              fullWidth
              variant="filled"
              InputLabelProps={{ shrink: true }}
            />

           

            <TextField
              label="Description"
              multiline
              minRows={3}
              value={editValues.description}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, description: e.target.value }))
              }
              fullWidth
              variant="filled"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={saving}
            variant="contained"
            color="secondary"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Item</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to delete it?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            color="error"
            variant="contained"
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

/* ---------- subcomponents ---------- */
function LabelValue({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {value ?? "-"}
      </Typography>
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}
